/** Codes compacts (URL, selects). */
export const PRODUCT_FORMAT_CODES = ["CV", "MX", "FC"] as const;

export type ProductFormatCode = (typeof PRODUCT_FORMAT_CODES)[number];

/** Libellés LMS uniquement (`Product.format` côté API reste fc / mx / cv). */
export const FORMAT_VALUE_BY_CODE: Record<ProductFormatCode, string> = {
    FC: "FC seule",
    CV: "100% CV",
    MX: "Mixte"
};

function lowerKeyToCode(): Map<string, ProductFormatCode> {
    const m = new Map<string, ProductFormatCode>();
    (Object.entries(FORMAT_VALUE_BY_CODE) as [ProductFormatCode, string][]).forEach(([code, storage]) => {
        m.set(storage.toLowerCase(), code);
    });
    // Legacy label kept for backward compatibility with stored values.
    m.set("fc seules", "FC");
    PRODUCT_FORMAT_CODES.forEach(code => m.set(code.toLowerCase(), code));
    return m;
}

const LOWER_TO_CODE = lowerKeyToCode();

/** Repasse une valeur API / produit vers le code canon (`fc` | `mx` | `cv`), ou null. */
export function productFormatToCode(raw: string | null | undefined): ProductFormatCode | null {
    if (raw == null || raw === "") return null;
    return LOWER_TO_CODE.get(raw.trim().toLowerCase()) ?? null;
}

/** Libellé LMS ; accepte code ou valeur stockée. */
export function getProductFormatLabel(raw: string | null | undefined): string {
    const code = productFormatToCode(raw);
    return code ? FORMAT_VALUE_BY_CODE[code] : (raw?.trim() ?? "");
}

/** Libellé badge format, avec prise en compte d'un créneau CV en fin de parcours FC. */
export function getProductFormatBadgeLabel(raw: string | null | undefined, hasVirtualClass = false): string {
    const code = productFormatToCode(raw);
    if (code === "FC" && hasVirtualClass) {
        return "FC+CV";
    }
    return getProductFormatLabel(raw);
}

/** Code court affichable (FC / FC+CV / MX / CV), ou null si format inconnu. */
export function getProductFormatShortCode(raw: string | null | undefined, hasVirtualClass = false): string | null {
    const code = productFormatToCode(raw);
    if (!code) return null;
    if (code === "FC" && hasVirtualClass) return "FC+CV";
    return code;
}

/** Libellé complet pour le tooltip, avec prise en compte d'un créneau CV en fin de parcours FC. */
export function getProductFormatFullLabel(raw: string | null | undefined, hasVirtualClass = false): string {
    const code = productFormatToCode(raw);
    if (code === "FC" && hasVirtualClass) return "FC + Classe virtuelle";
    return getProductFormatLabel(raw);
}

export const PRODUCT_FORMAT_FILTER_OPTIONS: { value: ProductFormatCode; label: string }[] = [
    { value: "FC", label: FORMAT_VALUE_BY_CODE.FC },
    { value: "MX", label: FORMAT_VALUE_BY_CODE.MX },
    { value: "CV", label: FORMAT_VALUE_BY_CODE.CV }
];

export function productFormatSelectValue(raw: string | null): "all" | ProductFormatCode {
    const code = productFormatToCode(raw);
    return code ?? "all";
}
